import { NodeStore } from "@/components/@types";
import { BoxForEntity } from "@/components/atoms/BoxForEntity";
import { Icon } from "@/components/atoms/Icon";
import { useNode } from "@/components/organisms/Node/Node.hooks";
import { EnumColors } from "@/constants/values";
import { useEnumColor } from "@/hooks/useEnumColor";
import { Objects } from "@/objects";
import { idToIdentifier } from "@/objects/@utils";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/components/ui/utils";
import _ from "lodash";
import type { CSSProperties } from "react";
import { StoreApi, UseBoundStore } from "zustand";

/**
 * A change to the record, said on its type icon: in the change's colour on its
 * soft ground, and named with the record's kind on hover ("Contact updated").
 */
export type NodeTypeIconChange = {
  /** What it is: ``create`` / ``update`` / ``delete``, or decided. */
  change: string;
  /** Said after the record's kind on hover: "created", "confirmed". */
  label: string;
  /** The app's colour for it, as an enum's chip wears it. */
  enumColor: (typeof EnumColors)[keyof typeof EnumColors];
};

export function NodeTitleLayoutEntityBox({
  store,
  size = "md",
  isLoading = false,
  isTypeIconVisible = false,
  typeIconChange = null,
  contextEntityIdentifier = null,
}: {
  store: UseBoundStore<StoreApi<NodeStore>>;
  /** Size variant. Default: "md" */
  size?: "sm" | "md";
  /** The record has not loaded: hold the box's place rather than leave a gap. */
  isLoading?: boolean;
  /** Show the record's type icon after the box, where records of many types are listed together. */
  isTypeIconVisible?: boolean;
  /** A change to the record, said on its type icon rather than in words beside it. */
  typeIconChange?: NodeTypeIconChange | null;
  /** The business the record is shown in: one of its own says nothing by its box. */
  contextEntityIdentifier?: string | null;
}) {
  const { fieldValues, metadata } = useNode(store);
  // Its colours for the scheme shown, as an enum's chip takes them.
  const changeColors = useEnumColor(
    typeIconChange?.enumColor ?? EnumColors.GREY_1,
  );
  const { typename } = metadata;
  const objectDefinition = Objects[typename];
  const entityId = objectDefinition?.getEntityId?.(fieldValues);
  const isOwnBusiness =
    entityId != null && idToIdentifier(entityId) === contextEntityIdentifier;
  // Map component size to BoxForEntity size
  const entityBoxSize = size === "sm" ? "md" : "lg";

  if (isLoading) {
    // The same square ``BoxForEntity`` draws at these sizes.
    const side = entityBoxSize === "md" ? 26 : 32;
    return (
      <Skeleton
        variant="rectangular"
        width={side}
        height={side}
        className="rounded shrink-0"
      />
    );
  }

  return (
    <>
      {entityId && !isOwnBusiness && (
        <div data-testid="node-title-entity" className="flex shrink-0">
          <BoxForEntity
            identifier={idToIdentifier(entityId)}
            representation="character"
            size={entityBoxSize}
          />
        </div>
      )}
      {isTypeIconVisible && objectDefinition?.ReactIcon && (
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              data-testid="node-title-type-icon"
              data-change={typeIconChange?.change}
              className={cn(
                "flex shrink-0",
                typeIconChange
                  ? "text-(--type-icon-color) bg-(--type-icon-background) rounded-sm p-[3px]"
                  : "text-muted-foreground/80",
              )}
              style={
                typeIconChange
                  ? ({
                      "--type-icon-color": changeColors.color,
                      "--type-icon-background": changeColors.backgroundColor,
                    } as CSSProperties)
                  : undefined
              }
            >
              <Icon ReactIcon={objectDefinition.ReactIcon} size={size} />
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            {[
              _.upperFirst(objectDefinition.humanName ?? ""),
              typeIconChange?.label,
            ]
              .filter(Boolean)
              .join(" ")}
          </TooltipContent>
        </Tooltip>
      )}
    </>
  );
}
